import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nb', label: 'Norsk', flag: '🇳🇴' },
  { code: 'ook', label: 'Ook!', flag: '🦧' },
] as const;

type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

function currentLanguageCode(lng: string | undefined): LanguageCode {
  const code = lng?.split('-')[0] ?? 'en';
  return SUPPORTED_LANGUAGES.some(language => language.code === code)
    ? (code as LanguageCode)
    : 'en';
}

export function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const language =
    SUPPORTED_LANGUAGES.find(
      item => item.code === currentLanguageCode(i18n.language)
    ) ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleChangeLanguage = (code: LanguageCode) => {
    void i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-uphone-language border border-uphone-language-border',
          'bg-white/90 px-2.5 py-1.5 text-uphone-language-text',
          'hover:bg-white focus:outline-none focus:ring-2 focus:ring-white/50'
        )}
        aria-label={t('common.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(isOpen => !isOpen)}
      >
        <span aria-hidden className="text-uphone-language-flag">
          {language.flag}
        </span>
        <span className="text-uphone-language-code uppercase">{language.code}</span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="fixed inset-0 z-40 cursor-default bg-black/30"
            onClick={() => setOpen(false)}
          />
          <ul
            role="listbox"
            aria-label={t('common.language')}
            className="absolute right-0 top-full z-50 mt-2 min-w-uphone-language-menu overflow-hidden rounded-uphone-language-menu bg-white shadow-uphone-language-menu"
          >
            {SUPPORTED_LANGUAGES.map(item => {
              const selected = item.code === language.code;
              return (
                <li key={item.code} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={clsx(
                      'flex w-full items-center gap-2.5 px-4 py-3 text-left text-uphone-language-option text-uphone-language-label',
                      'hover:bg-unilink-surface-hover',
                      selected && 'bg-uphone-language-active'
                    )}
                    onClick={() => handleChangeLanguage(item.code)}
                  >
                    <span aria-hidden className="text-uphone-language-option-flag">
                      {item.flag}
                    </span>
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}
